/* UI-DESIGN: the catalogue pairs each case image with plain facts on one
   surface. Shared homepage chapters and hosting behavior stay unchanged. */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";
import { ArrowRight, Persons } from "@gravity-ui/icons";
import { useGetMysteriesQuery } from "../../reducers/apiSlice";
import LightboxSurface from "../../components/lightbox/LightboxSurface";
import GlassPanel from "../../components/lightbox/GlassPanel";
import LightboxButton from "../../components/lightbox/LightboxButton";
import { productFor } from "../../components/lightbox/caseProduct";
import SlideMount from "../../components/lightbox/SlideMount";
import RolePreview from "../../components/lightbox/RolePreview";
import { FALLBACK_CASES, toCaseView } from "../../components/lightbox/caseView";
import type { CaseView } from "../../components/lightbox/caseView";
import { caseArtFor } from "../../components/lightbox/caseArt";
import TypeReview from "../../components/lightbox/TypeReview";
import { initialTypeface } from "../../components/lightbox/typefaces";
import type { Typeface } from "../../components/lightbox/typefaces";
import styles from "./Mysteries.module.scss";

const noop = () => undefined;

const Mysteries = () => {
  const navigate = useNavigate();
  /* UI-DESIGN: LB-56 the Cases page is the second real composition for
     the opt-in type review; normal /mysteries keeps the current typography. */
  const [typeface, setTypeface] = useState(initialTypeface);
  const reviewType = new URLSearchParams(window.location.search).has("type");
  const chooseTypeface = (value: Typeface) => {
    setTypeface(value);
    const url = new URL(window.location.href);
    url.searchParams.set("type", value);
    history.replaceState(null, "", url);
  };
  const { data: mysteries, isLoading, isFetching, isError, refetch } = useGetMysteriesQuery(undefined);
  const liveCases: CaseView[] = (mysteries ?? []).map(toCaseView);
  /* UI-DESIGN: as on the Home, the local fallback only when the query
     errors, and labelled */
  const cases = isError ? FALLBACK_CASES : liveCases;
  const [previewing, setPreviewing] = useState<CaseView | null>(null);

  const hostCase = (view: CaseView) => {
    /* UI-DESIGN: preview records are never passed into the hosting form */
    if (isError || !liveCases.some((c) => c.id === view.id)) return;
    navigate(`/create-game?mystery=${encodeURIComponent(view.id)}`);
  };

  return (
    <LightboxSurface mode="static" typeface={typeface} onUnavailable={noop}>
      <main className={styles.page}>
        {/* UI-DESIGN: LB-85 lead with facts available in the mystery record. */}
        <section className={styles.intro} aria-labelledby="lb-cases-page-title">
          <header className={styles.introPanel}>
            <h1 id="lb-cases-page-title" className={clsx(styles.title, styles.pageTitle)}>
              The mystery files
            </h1>
            <p className={styles.sub}>
              Pick a setting. Bring your suspects.
            </p>
          </header>
        </section>

        {isError ? (
          <div className={styles.note}>
            <p role="status">These are preview cases. Reload live cases to start hosting.</p>
            <LightboxButton isPending={isFetching} onPress={() => { void refetch(); }}>
              Reload cases
            </LightboxButton>
          </div>
        ) : null}

        <section className={styles.cases} aria-label="Cases">
          {isLoading ? (
            <p className={styles.note} role="status" aria-busy="true">
              Developing the slides…
            </p>
          ) : cases.length > 0 ? (
            cases.map((view) => {
              const artwork = caseArtFor(view.name) ?? view.imageUrl;
              const product = productFor(view);
              return (
                /* UI-DESIGN: catalogue-only composition leaves the homepage's case objects unchanged. */
                <GlassPanel as="article" key={view.id} className={styles.catalogueCase}
                  data-lb-chapter="case" data-case-id={view.id} aria-labelledby={`catalogue-${view.id}`}>
                  <div className={styles.art}>
                    {artwork ? <img src={artwork} alt="" loading="lazy" decoding="async" /> : <SlideMount view={view} />}
                  </div>
                  <div className={styles.caseDetails}>
                    <h2 id={`catalogue-${view.id}`}>{view.name}</h2>
                    {product.hook && <p>{product.hook}</p>}
                    <dl className={styles.caseFacts}>
                      {view.players && <div><dt>Players</dt><dd>{view.players}</dd></div>}
                      {view.length && <div><dt>Rounds</dt><dd>{view.length}</dd></div>}
                      {product.difficulty && <div><dt>Difficulty</dt><dd>{product.difficulty}</dd></div>}
                    </dl>
                    <div className={styles.caseActions}>
                      <LightboxButton tone="primary" isDisabled={isError} onPress={() => hostCase(view)}>Try this case</LightboxButton>
                      <LightboxButton tone="quiet" onPress={() => setPreviewing(view)}>Preview a role</LightboxButton>
                    </div>
                  </div>
                </GlassPanel>
              );
            })
          ) : (
            <p className={styles.note} role="status">
              No cases are published yet.
            </p>
          )}
        </section>

        <section className={styles.hostRow} aria-labelledby="lb-cases-host-title">
          <GlassPanel className={clsx(styles.card, styles.hostCard)}>
            <h2 id="lb-cases-host-title" className={styles.title}>
              Found the one?
            </h2>
            <p className={styles.sub}>Choose your mystery, name your party and send the invite link.</p>
            <div className={styles.actions}>
              <LightboxButton tone="primary" icon={<Persons />} trailing={<ArrowRight />} onPress={() => navigate("/create-game")}>
                Host a party
              </LightboxButton>
              <LightboxButton tone="secondary" onPress={() => navigate("/about")}>
                How it works
              </LightboxButton>
            </div>
          </GlassPanel>
        </section>
      </main>
      <RolePreview view={previewing} onClose={() => setPreviewing(null)} onHost={hostCase} canHost={!isError} />
      {reviewType ? <TypeReview value={typeface} onChange={chooseTypeface} /> : null}
    </LightboxSurface>
  );
};

export default Mysteries;
